
import { defineComponent } from 'vue';
import { CalendarDay, Slot } from '@/utils/interfaces';
import { SlotStatus } from '@/utils/enums';

enum ViewPort {
  MOBILE = 'mobile',
  TABLET = 'tablet',
  WEB = 'web'
}

enum ViewPortMaxWidth {
  MOBILE = 768,
  TABLET = 992
}
export default defineComponent({
  name: 'Calendar',
  props: {
    clickedSlots: {
      type: Array as () => Slot[],
      default: () => [
        {
          date: ''
        }
      ]
    },
    days: {
      type: Array as () => CalendarDay[],
      default: () => []
    },
    mobileColumns: {
      type: Number,
      default: 2
    },
    tabletColumns: {
      type: Number,
      default: 5
    },
    webColumns: {
      type: Number,
      default: 3
    },
    compressedView: {
      type: Boolean,
      default: false
    }
  },
  emits: ['on-slots-change'],
  data() {
    return {
      controler: 0,
      currentWidth: 0
    };
  },
  computed: {
    daysLength(): number {
      return this.days.length;
    },
    allowToIncrease(): boolean {
      return this.controler + (this.columns) < this.datesLength;
    },
    allowToDecrease(): boolean {
      return this.controler > 0;
    },
    allowToIncreaseClass(): string | null {
      return this.allowToIncrease ? null : 'calendar__arrow-button--grey';
    },
    allowToDecreaseClass(): string | null {
      return this.allowToDecrease ? null : 'calendar__arrow-button--grey';
    },
    isMobileView(): boolean {
      return this.currentWidth < ViewPortMaxWidth.MOBILE;
    },
    viewPortType(): ViewPort {
      if (this.currentWidth < ViewPortMaxWidth.MOBILE) return ViewPort.MOBILE;
      else if (this.currentWidth < ViewPortMaxWidth.TABLET) return ViewPort.TABLET;
      else return ViewPort.WEB;
    },
    columns(): number {
      switch (this.viewPortType) {
        case ViewPort.MOBILE: return this.mobileColumns;
        case ViewPort.TABLET: return this.tabletColumns;
        default: return this.webColumns;
      }
    },
    datesLength(): number {
      return this.days.length;
    }
  },
  watch: {
    viewPortType() {
      this.controler = 0;
    }
  },
  created() {
    this.setInitialWidth();
    addEventListener('resize', this.onScreenResize);
  },
  mounted() {
    if (this.clickedSlots.length) {
      this.scrollToChosenSlot();
    } else {
      this.scrollToFirstAvailable();
    }
  },
  beforeUnmount() {
    removeEventListener('resize', this.onScreenResize);
  },
  methods: {
    setInitialWidth(): void {
      this.currentWidth = window.innerWidth;
    },
    onScreenResize(): void {
      this.currentWidth = window.innerWidth;
      this.$forceUpdate();
    },
    checkClass(slot: Slot): boolean {
      return !!this.clickedSlots.find((item: Slot) => item.fullDate === slot.fullDate);
    },
    checkStatus(slot: Slot): boolean {
      return slot.status === SlotStatus.RESERVED;
    },
    increase(): void {
      if (this.allowToIncrease) {
        this.controler += 1;
      }
    },
    decrease(): void {
      if (this.allowToDecrease) {
        this.controler -= 1;
      }
    },
    scrollToElement(className: string): void {
      setTimeout(() => {
        const scrollWrapper = (this.$refs.scrollWrapper as HTMLElement);
        const initialSlot = scrollWrapper?.getElementsByClassName(className)[0];

        if (initialSlot instanceof HTMLElement) {
          const initialSlotOffset = initialSlot.offsetTop;
          scrollWrapper.scrollTo(0, initialSlotOffset);
        }
      }, 500);
    },
    scrollToChosenSlot() {
      this.scrollToElement('calendar__slot--clicked');
    },
    scrollToFirstAvailable() {
      this.scrollToElement('calendar__slot--available');
    },
    onSlotChange(slot: Slot): void {
      if (slot.status === SlotStatus.AVAILABLE) {
        this.$emit('on-slots-change', slot);
      }
    },
    slotClasses(slot: Slot) {
      const arr = [];
      if (this.checkClass(slot)) {
        arr.push('calendar__slot--clicked');
      }
      if (this.compressedView) {
        arr.push('calendar__slot--compressed');
      }
      if (this.checkStatus(slot)) {
        arr.push('calendar__slot--reserved');
      } else {
        arr.push('calendar__slot--available');
      }
      return arr;
    },
    resetControler(): void {
      this.controler = 0;
    }
  }
});
